import {createContext, useCallback, useContext, useMemo, useState } from "react";
import { getWrfImages, type WrfVar } from "../services/wrf.service";

type Ctx = {
  imagesAll: string[];
  visibleImages: string[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  variable: WrfVar;
  setVariable: (v: WrfVar) => void;
  loading: boolean;
  error: string | null;
  latestFolder: string | null;
  fetchWrfImages: (v?: WrfVar) => Promise<void>;
};

const WrfImagesContext = createContext<Ctx | null>(null);
const API_URL = import.meta.env.VITE_API_URL;

export function WrfImagesProvider({ children }: { children: React.ReactNode }) {
  // ✅ sem isto, não existem sets
  const [imagesAll, setImagesAll] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [variable, setVariable] = useState<WrfVar>("VENTO_10M");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestFolder, setLatestFolder] = useState<string | null>(null);

  const fetchWrfImages = useCallback(async (v?: WrfVar) => {
  const target = v || variable;

  setLoading(true);
  setError(null);

  try {
    const { list, latestFolder } = await getWrfImages({
      apiUrl: API_URL,
      variable: target,
    });

    if (!Array.isArray(list) || list.length === 0) {
      throw new Error(`Nenhuma imagem encontrada para ${target}.`);
    }

    setImagesAll(list);
    setLatestFolder(latestFolder);
    setCurrentIndex(0);

    localStorage.setItem(
      `wrf_${target}`,
      JSON.stringify({
        variable: target,
        latestFolder,
        images: list,
        savedAt: Date.now(),
      })
    );
  } catch (e: any) {
    setError(e?.message || String(e));
    setImagesAll([]);
    setLatestFolder(null);
    setCurrentIndex(0);
  } finally {
    setLoading(false);
  }
}, [variable]);

  // Intervalo de tempo fixo: sempre os 3 dias completos disponíveis.
  const visibleImages = imagesAll;

  const value = useMemo(
    () => ({
      imagesAll,
      visibleImages,
      currentIndex,
      setCurrentIndex,
      variable,
      setVariable,
      loading,
      error,
      latestFolder,
      fetchWrfImages,
    }),
    [imagesAll, visibleImages, currentIndex, variable, loading, error, latestFolder, fetchWrfImages]
  );

  return <WrfImagesContext.Provider value={value}>{children}</WrfImagesContext.Provider>;
}

export function useWrfImages() {
  const ctx = useContext(WrfImagesContext);
  if (!ctx) throw new Error("useWrfImages deve ser usado dentro de WrfImagesProvider");
  return ctx;
}
